import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { BodyText, Icon } from '@sg/garnish';

import { TooltipWithBanner } from '../../../../../TooltipWithBanner';
import { LOCATION_RESULTS_OTHER_LOCATIONS_TITLE_HEIGHT } from '../../LocationResults.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsOtherLocationsTitle = memo(
  (props: LocationResultsOtherLocationsTitleProps) => {
    const { otherLocationSearchType } = props;

    const { formatMessage } = useIntl();

    return (
      <View style={styles.container}>
        <BodyText size={2} numberOfLines={1} style={styles.label}>
          {formatMessage(messages.locationResultOtherLocations, {
            locationSearchType: otherLocationSearchType,
          })}
        </BodyText>

        {otherLocationSearchType === 'outpost' ? (
          <TooltipWithBanner label={formatMessage(messages.locationResultOutpostHint)}>
            <Icon name="IconHelp" />
          </TooltipWithBanner>
        ) : null}
      </View>
    );
  },
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationResultOtherLocations: {
    defaultMessage: `{locationSearchType, select,
    pickup {Sweetgreen locations}
    delivery {Delivery locations}
    outpost {Outpost locations}
    other {Other locations}
  }`,
    description: 'Location | Result | Other Locations',
  },
  locationResultOutpostHint: {
    defaultMessage:
      'Sweetgreen Outpost is a batch delivery program that brings locally sourced food to your office every day.',
    description: 'Location | Result | Outpost Hint',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    height: LOCATION_RESULTS_OTHER_LOCATIONS_TITLE_HEIGHT,
    marginHorizontal: theme.spacing['4'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
  },
  label: {
    marginRight: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsOtherLocationsTitleProps = Readonly<{
  otherLocationSearchType: 'pickup' | 'delivery' | 'outpost';
}>;
